// Unicode-scalar arrays (the Swift's `Array(text.unicodeScalars)`), the scalar and `Character` properties the checks
// test, and NSRange helpers: what QuoteMarks, TextCleanup, the style linter and the statistics stand on.

/** `Array(text.unicodeScalars)`: one entry per scalar. */
export const toScalars = (text: string): string[] => Array.from(text);
/** `String(String.UnicodeScalarView(s))`. */
export const fromScalars = (s: readonly string[], from = 0, to = s.length): string => s.slice(from, to).join('');

const ALPHABETIC = /^\p{Alphabetic}$/u;
const WHITE_SPACE = /^\p{White_Space}$/u;
const LOWERCASE = /^\p{Lowercase}$/u;
const UPPERCASE = /^\p{Uppercase}$/u;

/** `Unicode.Scalar.Properties.isAlphabetic`. */
export const isAlphabetic = (c: string | undefined): boolean => c !== undefined && ALPHABETIC.test(c);
/** `properties.isWhitespace`. */
export const isWhitespace = (c: string | undefined): boolean => c !== undefined && WHITE_SPACE.test(c);
/** `properties.isLowercase`. */
export const isLowercase = (c: string | undefined): boolean => c !== undefined && LOWERCASE.test(c);
/** `properties.isUppercase`. */
export const isUppercase = (c: string | undefined): boolean => c !== undefined && UPPERCASE.test(c);
/** `("0" ... "9").contains(c)`. */
export const isDigit = (c: string | undefined): boolean => c !== undefined && c.length === 1 && c >= '0' && c <= '9';

/** Titlecase mappings that differ from the uppercase one (UnicodeData/SpecialCasing). */
const TITLECASE: Record<string, string> = {
  'ß': 'Ss', 'ǆ': 'ǅ', 'ǅ': 'ǅ', 'Ǆ': 'ǅ', 'ǉ': 'ǈ', 'ǈ': 'ǈ', 'Ǉ': 'ǈ', 'ǌ': 'ǋ', 'ǋ': 'ǋ', 'Ǌ': 'ǋ', 'ǳ': 'ǲ', 'ǲ': 'ǲ', 'Ǳ': 'ǲ',
  'ﬀ': 'Ff', 'ﬁ': 'Fi', 'ﬂ': 'Fl', 'ﬃ': 'Ffi', 'ﬄ': 'Ffl', 'ﬅ': 'St', 'ﬆ': 'St',
};

/** `properties.titlecaseMapping`. */
export const titlecase = (c: string): string => TITLECASE[c] ?? c.toUpperCase();

/** `Character.isUppercase`: changes when lowercased, not when uppercased. */
export const charIsUppercase = (c: string | undefined): boolean => !!c && c.toUpperCase() === c && c.toLowerCase() !== c;
/** `Character.isLowercase`: changes when uppercased, not when lowercased. */
export const charIsLowercase = (c: string | undefined): boolean => !!c && c.toLowerCase() === c && c.toUpperCase() !== c;

// MARK: NSRange helpers (UTF-16, [location, location + length) as [from, to))

export type Range16 = [number, number];
export const intersects = (a: Range16, b: Range16) => Math.min(a[1], b[1]) - Math.max(a[0], b[0]) > 0;
/** `NSLocationInRange`. */
export const locationIn = (location: number, r: Range16) => location >= r[0] && location < r[1];
